import React, { useState } from 'react';
import { useStacklineTheme } from '@stackline/ui';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { RatingsAndReviewMetric } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewMetricTypes';
import { CustomXAxisLabel } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewRatings/ProductCatalogCountRatingVolumeChart';
import {
  parseCumulativeProductCatalogCountRatingVolumeResponse,
  useProductCatalogCountRatingData
} from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useAppSelector } from 'src/utils/Hooks';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const productCatalogCountOptions = [
  { label: 'Product Catalog Count by Cumulative', id: 'cumulativeProductCatalogCount' }
];

/**
 * Product Catalog Count by Cumulative Review Volume Chart for Review tab.
 *
 */
const ReviewProductCatalogCountByCumulativeVolumeChart = () => {
  const theme = useStacklineTheme();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const [selectedMetric, setSelectedMetric] = useState('cumulativeProductCatalogCount');
  const normalizedSelectedMetric = productCatalogCountOptions.some((option) => option.id === selectedMetric)
    ? selectedMetric
    : 'cumulativeProductCatalogCount';
  const shouldShowMetricDropdown = productCatalogCountOptions.length > 1;
  const selectedMetricLabel =
    productCatalogCountOptions.find((option) => option.id === normalizedSelectedMetric)?.label ||
    productCatalogCountOptions[0].label;
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const { endWeek: comparisonEndWeek } = useAppSelector((state) => state.comparisonTimePeriod);

  const { data, isLoading: cumulativeLoading } = useProductCatalogCountRatingData(
    RatingsAndReviewMetric.CUMULATIVE_REVIEW_VOLUME
  );

  const { currentData } = data
    ? parseCumulativeProductCatalogCountRatingVolumeResponse({
        data: data as any,
        comparisonEndWeek,
        metricName: 'Reviews'
      })
    : { currentData: [] };
  const chartData = currentData as [string, number][];

  return (
    <BeaconChartWithLegend
      title={`${selectedMetricLabel} Review Volume Tier`}
      includeSummaryInfo={false}
      convertSeriesToDelimitedData={async () => {
        return saveSingleBarChartAsDelimitedData(chartData, { metricLabel: 'Product Catalog Count' });
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the number of products for ${entityPhrase} `}
            {taxonomyText} in each review volume tier.
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      singleDropdownProps={
        shouldShowMetricDropdown
          ? {
              defaultLabel: '',
              options: productCatalogCountOptions,
              selectedId: normalizedSelectedMetric,
              onChange: (option) => setSelectedMetric(option.id),
              suffix: 'Review Volume Tier'
            }
          : undefined
      }
      primaryLegendProps={{
        displayName: legendLabel
      }}
      loading={cumulativeLoading}
      loadingComponent={<TopEntityChartV2Loading />}
    >
      <TopEntityChartV2
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        seriesColors={[theme.colors.primary]}
        xAxisFormatter={(xAxisCategory) => {
          if (xAxisCategory.name === '400 and Above') {
            return ReactDOMServer.renderToStaticMarkup(<CustomXAxisLabel xAxisCategory={xAxisCategory} />);
          }
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: chartData,
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default ReviewProductCatalogCountByCumulativeVolumeChart;
